import { Component, DestroyRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { API, AreaDTO, ComparisonOperator, GeoCullAtomDTO, GeoCullDTO, PolygonDTO } from '../../../services/api/api';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { UtilService } from '../../../services/util.service';
import { AreaEditComponent } from '../../../pages/datasets/area-edit/area-edit.component';
import { ToasterService, ToastType } from '../../../services/toaster/toaster.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';

interface EnhancedArea {
  id: number;
  areas: AreaDTO[];
  inverted: boolean;
}

interface AtomInterface extends GeoCullAtomDTO {
  id: any;
  type: string;
  field: string;
  valid: boolean;
  values: string[];
}

@Component({
  selector: 'sb-filter-geo-cull',
  templateUrl: './sb-filter-geo-cull.component.html',
  styleUrls: ['./sb-filter-geo-cull.component.scss'],
  standalone: false,
})
export class SbFilterGeoCullComponent implements OnInit {
  @Input() cull: GeoCullDTO;
  @Output() cullChange: EventEmitter<GeoCullDTO> = new EventEmitter<GeoCullDTO>();

  loading: boolean;

  @Input() easyMode: boolean;

  @Input() disabled: boolean = false;

  @Input() tabTwo: boolean;

  @Input() isInSidebarContext: boolean;

  polygons: PolygonDTO[];
  negPolygons: PolygonDTO[];

  selectedEnhancedAreas: EnhancedArea[];
  selectedAreas: AreaDTO[];

  constructor(
    public modal: ModalService,
    public util: UtilService,
    public toastHandler: ToasterService,
    public api: API,
    private destroyRef: DestroyRef
  ) {
    this.loading = true;
    this.selectedAreas = [];
    this.selectedEnhancedAreas = [];
    this.polygons = [];
    this.negPolygons = [];
  }

  ngOnInit(): void {
    if (this.easyMode) {
      if (this.cull?.atoms?.length > 0) {
        const areaIDs: number[] = [];
        for (const atom of this.cull.atoms) {
          areaIDs.push(Number(...(atom as AtomInterface).values));
        }
        this.api
          .dm_geo_Area_retrieveOfficial({ id: areaIDs })
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            take(1),
            finalize(() => {
              this.loading = false;
            })
          )
          .subscribe((areas: AreaDTO[]) => {
            if (areas.length > 0) {
              for (const atom of this.cull.atoms) {
                const tempAreaArray: AreaDTO[] = [];
                let atomInverted: boolean = false;
                for (const atomAreaID of (atom as AtomInterface).values) {
                  const index = this.util.arrayToIdArray(areas).indexOf(parseInt(atomAreaID, 10));
                  if (index >= 0) {
                    tempAreaArray.push(areas[index]);
                  }
                }
                if (tempAreaArray.length > 0) {
                  const positionInExpression = this.cull.expression.indexOf(atom.id.num.toString());
                  if (positionInExpression > 0) {
                    if (this.cull.expression.charAt(positionInExpression - 1) === '!') {
                      atomInverted = true;
                    }
                  }
                  for (const tempArea of tempAreaArray) {
                    this.util.geo.cachedPolygons.push(...tempArea.polygons);
                    this.util.geo.getCachedPolygons(this.util.arrayToIdArray(tempArea.polygons)).then((result: PolygonDTO[]) => {
                      tempArea.polygons = result;
                      if (atomInverted) {
                        this.negPolygons.push(...result);
                      } else {
                        this.polygons.push(...result);
                      }
                    });
                  }
                  this.selectedEnhancedAreas.push({ areas: tempAreaArray, inverted: atomInverted, id: atom.id.num });
                  this.selectedAreas.push(...tempAreaArray);
                }
              }
            }
          });
      }

      this.loading = false;
    } else {
    }
  }

  createAreaRow() {
    this.modal
      .showNavigationModal({
        entryComponent: { value: AreaEditComponent, data: { pauseNotifications: false } },
        title: null,
        modalOptions: {
          windowClass: 'bigXL',
        },
      })
      .then((result: AreaDTO) => {
        if (result?.id) {
          const pId = this.selectedEnhancedAreas.length + 1;
          this.selectedAreas.push(result);
          this.selectedEnhancedAreas.push({ areas: [result], inverted: false, id: pId });
          this.util.geo.cachedPolygons.push(...result.polygons);
          this.updatePolygons();
          this.updateDTO();
        }
      })
      .catch(() => {});
  }

  checkSelection(areaArray: AreaDTO[]): AreaDTO[] {
    if (areaArray && areaArray.length <= 0) {
      return [];
    }
    const tempArray: AreaDTO[] = [];
    const doubleSelection: AreaDTO[] = [];
    for (const singleArea of areaArray) {
      if (!this.selectedAreas.find(item => item.id === singleArea.id)) {
        tempArray.push(singleArea);
      } else {
        doubleSelection.push(singleArea);
      }
    }
    if (doubleSelection && doubleSelection.length > 0) {
      const areasString: string = doubleSelection.join(', ');
      this.toastHandler.toast(ToastType.DANGER, 'Already selected areas: ' + areasString);
    }
    return tempArray;
  }

  addAreaRow() {
    this.modal
      .modal(
        {
          title: '',
          modalOptions: { windowClass: 'bigXL' },
        },
        {
          templateId: 'areaSelectionModal',
        }
      )
      .then((resultArray: AreaDTO[]) => {
        if (resultArray && resultArray.length > 0) {
          const pId = this.selectedEnhancedAreas.length + 1;
          const tempAreaArray = this.checkSelection(resultArray);
          if (tempAreaArray && tempAreaArray.length > 0) {
            this.selectedEnhancedAreas.push({ areas: tempAreaArray, inverted: false, id: pId });
            this.selectedAreas.push(...tempAreaArray);
            for (const area of tempAreaArray) {
              this.util.geo.cachedPolygons.push(...area.polygons);
            }
            this.updatePolygons();
            this.updateDTO();
          }
        }
      })
      .catch(() => {});
  }

  removeEnhancedArea(index: number) {
    if (index < this.selectedEnhancedAreas.length && index >= 0) {
      let indexAreas = 0;
      for (let i = 0; i < index; i++) {
        indexAreas += this.selectedEnhancedAreas[i].areas.length;
      }
      this.selectedAreas.splice(indexAreas, this.selectedEnhancedAreas[index].areas.length);
      this.selectedEnhancedAreas.splice(index, 1);
      this.updatePolygons();
      this.updateDTO();
    }
  }

  emitChange(cull?: GeoCullDTO) {
    this.cullChange.emit(cull ? cull : this.cull);
  }

  toggleInverted($event, enhancedArea: EnhancedArea) {
    $event.preventDefault();
    $event.stopPropagation();
    enhancedArea.inverted = !enhancedArea.inverted;
    this.updatePolygons();
    this.updateDTO();
  }

  toggleGlobalInverted() {
    this.updateDTO();
  }

  updateDTO() {
    this.createAtoms();
    this.emitChange();
  }

  createAtoms() {
    this.cull.atoms = [];

    let id: number = 1;
    let expression: string = '';
    const lastElement = this.selectedEnhancedAreas[this.selectedEnhancedAreas.length - 1];
    for (const enhancedArea of this.selectedEnhancedAreas) {
      const atom: AtomInterface = {
        id: { num: -1, cull: null },
        type: 'GeoByAreaAtom',
        field: 'id',
        operator: ComparisonOperator.Equal,
        valid: true,
        values: [],
      };
      const idStringArray: string[] = [];
      atom.values = [];
      for (const singleArea of enhancedArea.areas) {
        idStringArray.push(singleArea.id.toString());
      }
      atom.id.num = id;
      atom.id.cull = { id: this.cull ? this.cull.id : null };
      atom.values.push(...idStringArray);
      if (!enhancedArea.inverted) {
        expression += id;
      } else {
        expression += '!' + id;
      }
      if (enhancedArea.id !== lastElement.id) {
        expression += '&';
      }
      this.cull.atoms.push(atom);
      id++;
    }
    this.cull.expression = expression;
  }

  updatePolygons() {
    this.polygons = [];
    this.negPolygons = [];
    for (const enhancedArea of this.selectedEnhancedAreas) {
      for (const area of enhancedArea.areas) {
        if (enhancedArea.inverted) {
          this.negPolygons.push(...area.polygons);
        } else {
          this.polygons.push(...area.polygons);
        }
      }
    }
  }
}
